import { backendApi } from "@/services/backend/api";
import {
  AddSectionRequest,
  CreatedResponse,
  CreateTextBlockRequest,
  DeleteTextBlockRequest,
  EmptyResponse,
  GetSettingsRequest,
  GetTextBlockRequest,
  ListSectionsRequest,
  ListSectionsResponse,
  ListTextBlocksRequest,
  ListTextBlocksResponse,
  PreviewServiceProgramPDFRequest,
  PutSettingsRequest,
  ReorderTextBlocksRequest,
  SettingsResponse,
  TextBlockResponse,
  UpdateTextBlockAlwaysDisplayRequest,
  UpdateTextBlockBodyRequest,
  UpdateTextBlockTitleRequest,
} from "@/services/backend/qpm/offer-config/types/offer-config";
import { previewResponseHandler } from "@/services/backend/_lib/pdf-helpers";

const qpmOfferConfigApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "QpmOfferConfigSettings",
      "QpmOfferConfigTextBlock",
      "QpmOfferConfigSection",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmOfferConfigGetSettings: build.query<
        SettingsResponse,
        GetSettingsRequest
      >({
        query: () => ({
          url: `/qpm/offer-config/settings/get`,
          method: "GET",
        }),
        providesTags: [{ type: "QpmOfferConfigSettings", id: "SINGLE" }],
      }),

      qpmOfferConfigPutSettings: build.mutation<
        EmptyResponse,
        PutSettingsRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/settings`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmOfferConfigSettings", id: "SINGLE" }],
      }),
      qpmOfferConfigServiceProgramPreviewPDF: build.query<
        { objectURL: string },
        PreviewServiceProgramPDFRequest
      >({
        query: () => ({
          url: `/qpm/offer-config/service-program/pdf`,
          method: "GET",
          headers: { Accept: "application/pdf" },
          responseHandler: previewResponseHandler,
          cache: "no-cache",
        }),
      }),

      qpmOfferConfigCreateTextBlock: build.mutation<
        CreatedResponse,
        CreateTextBlockRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmOfferConfigTextBlock", id: "LIST" }],
      }),

      qpmOfferConfigGetTextBlock: build.query<
        TextBlockResponse,
        GetTextBlockRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/get`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [{ type: "QpmOfferConfigTextBlock", id: result.id }]
            : [{ type: "QpmOfferConfigTextBlock", id: "LIST" }],
      }),

      qpmOfferConfigListTextBlocks: build.query<
        ListTextBlocksResponse,
        ListTextBlocksRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/list`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "QpmOfferConfigTextBlock", id: "LIST" },
                ...result.items.map((it) => ({
                  type: "QpmOfferConfigTextBlock" as const,
                  id: it.id,
                })),
              ]
            : [{ type: "QpmOfferConfigTextBlock", id: "LIST" }],
      }),

      qpmOfferConfigUpdateTextBlockBody: build.mutation<
        EmptyResponse,
        UpdateTextBlockBodyRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/body`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmOfferConfigTextBlock", id: "LIST" },
          { type: "QpmOfferConfigTextBlock", id: arg.id },
        ],
      }),

      qpmOfferConfigUpdateTextBlockTitle: build.mutation<
        EmptyResponse,
        UpdateTextBlockTitleRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/title`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmOfferConfigTextBlock", id: "LIST" },
          { type: "QpmOfferConfigTextBlock", id: arg.id },
        ],
      }),

      qpmOfferConfigUpdateTextBlockAlwaysDisplay: build.mutation<
        EmptyResponse,
        UpdateTextBlockAlwaysDisplayRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/always-display`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmOfferConfigTextBlock", id: "LIST" },
          { type: "QpmOfferConfigTextBlock", id: arg.id },
        ],
      }),

      qpmOfferConfigDeleteTextBlock: build.mutation<
        EmptyResponse,
        DeleteTextBlockRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmOfferConfigTextBlock", id: "LIST" },
          { type: "QpmOfferConfigTextBlock", id: arg.id },
        ],
      }),

      qpmOfferConfigReorderTextBlocks: build.mutation<
        EmptyResponse,
        ReorderTextBlocksRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/text-blocks/reorder`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmOfferConfigTextBlock", id: "LIST" }],
      }),

      qpmOfferConfigAddSection: build.mutation<
        EmptyResponse,
        AddSectionRequest
      >({
        query: (request) => ({
          url: `/qpm/offer-config/sections`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          { type: "QpmOfferConfigSection", id: "LIST" },
          { type: "QpmOfferConfigTextBlock", id: "LIST" },
        ],
      }),

      qpmOfferConfigListSections: build.query<
        ListSectionsResponse,
        ListSectionsRequest
      >({
        query: () => ({
          url: `/qpm/offer-config/sections/list`,
          method: "GET",
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "QpmOfferConfigSection", id: "LIST" },
                ...result.items.map((section) => ({
                  type: "QpmOfferConfigSection" as const,
                  id: section,
                })),
              ]
            : [{ type: "QpmOfferConfigSection", id: "LIST" }],
      }),
    }),
  });

export const {
  useQpmOfferConfigGetSettingsQuery,
  useQpmOfferConfigPutSettingsMutation,
  useLazyQpmOfferConfigServiceProgramPreviewPDFQuery,
  useQpmOfferConfigCreateTextBlockMutation,
  useQpmOfferConfigGetTextBlockQuery,
  useQpmOfferConfigListTextBlocksQuery,
  useQpmOfferConfigUpdateTextBlockBodyMutation,
  useQpmOfferConfigUpdateTextBlockTitleMutation,
  useQpmOfferConfigUpdateTextBlockAlwaysDisplayMutation,
  useQpmOfferConfigDeleteTextBlockMutation,
  useQpmOfferConfigReorderTextBlocksMutation,
  useQpmOfferConfigAddSectionMutation,
  useQpmOfferConfigListSectionsQuery,
} = qpmOfferConfigApi;
